/**
 * The three duels: a fixed tree that branches on the first answer, with three
 * rotating variants per slot so the same pair is not shown to every cook.
 *
 * Q1 asks the base (protein vs vegetal). Q2 asks the weeknight time budget on
 * the branch the cook chose. Q3 asks the intensity on that same branch. Within
 * a pair only the asked factor differs (guarded by onboardingDeck.test.ts).
 */
import { deckEntry, ONBOARDING_DECK, type DeckSlot, type QuizDeckEntry } from '../data/onboardingDeck';
import { plateName, type Axis } from './tasteAxes';
import type { OnboardingPick } from '../redux/onboardingSlice';

export type Question = 1 | 2 | 3;
export type Variant = 0 | 1 | 2;
export type Winner = 'a' | 'b';

export interface PairCards {
  question: Question;
  axis: Axis;
  pairId: string;
  a: QuizDeckEntry;
  b: QuizDeckEntry;
}

export const AXIS_OF_QUESTION: Record<Question, Axis> = { 1: 'base', 2: 'time', 3: 'heat' };

function slotsFor(question: Question, answers: ReadonlyArray<Winner>): [DeckSlot, DeckSlot] {
  if (question === 1) return ['q1a', 'q1b'];
  const branch = answers[0] === 'b' ? 'veg' : 'meat';
  return question === 2 ? [`q2a-${branch}`, `q2b-${branch}`] : [`q3a-${branch}`, `q3b-${branch}`];
}

export function pairFor(question: Question, answers: ReadonlyArray<Winner>, variant: Variant): PairCards {
  const [slotA, slotB] = slotsFor(question, answers);
  return {
    question,
    axis: AXIS_OF_QUESTION[question],
    pairId: `${slotA}|${slotB}|v${variant}`,
    a: deckEntry(slotA, variant),
    b: deckEntry(slotB, variant),
  };
}

/** FNV-1a over the seed, mod 3. Stable per cook, spread across cooks. */
export function pickVariant(seed: string): Variant {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return (h % 3) as Variant;
}

export function reactionKey(question: Question, winner: Winner): string {
  return `onboarding.quiz.reaction.q${question}${winner}`;
}

export function questionKey(question: Question): string {
  return `onboarding.quiz.question.q${question}`;
}

/** A random seed for cooks with no account; persisted by the slice. */
export function newSeed(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export interface ReadingParams {
  plate1: string;
  other1: string;
  plate2: string;
  other2: string;
  min2: number;
  minOther2: number;
}

export function deckTitle(entry: QuizDeckEntry, lang: 'fr' | 'en'): string {
  return lang === 'en' && entry.titleEn ? entry.titleEn : entry.title;
}

/**
 * The plates the reading card quotes. Falls back to empty strings when a pick
 * is missing so the card can still render a sentence.
 */
export function readingParams(picks: ReadonlyArray<OnboardingPick>, lang: 'fr' | 'en'): ReadingParams {
  const byId = new Map(ONBOARDING_DECK.map((e) => [e.recipeId, e] as const));
  const q1 = picks.find((p) => p.question === 1);
  const q2 = picks.find((p) => p.question === 2);
  const name = (id?: string) => {
    const e = id ? byId.get(id) : undefined;
    return e ? plateName(deckTitle(e, lang)) : '';
  };
  const minutes = (id?: string) => (id ? byId.get(id)?.minutes ?? 0 : 0);
  const win = (p?: OnboardingPick) => (p ? (p.winner === 'a' ? p.cardA : p.cardB) : undefined);
  const lose = (p?: OnboardingPick) => (p ? (p.winner === 'a' ? p.cardB : p.cardA) : undefined);
  return {
    plate1: name(win(q1)),
    other1: name(lose(q1)),
    plate2: name(win(q2)),
    other2: name(lose(q2)),
    min2: minutes(win(q2)),
    minOther2: minutes(lose(q2)),
  };
}
